import { useState, useEffect, useRef } from "react";
import { NAV_ITEMS } from "../constants";
import { apiFetch } from "../utils/api";

// ── Sidebar nav icons — single-colour SVG (colour injected via `fill` prop) ──
const NavIcon = ({ name, size = 16, color = "currentColor" }) => {
  const s = { width: size, height: size, fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", flexShrink: 0 };
  const icons = {
    dashboard: (
      <svg viewBox="0 0 24 24" style={s}>
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
        <path d="M9 21V12h6v9" />
      </svg>
    ),
    clients: (
      <svg viewBox="0 0 24 24" style={s}>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
    employees: (
      <svg viewBox="0 0 24 24" style={s}>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
    resources: (
      <svg viewBox="0 0 24 24" style={s}>
        <circle cx="9" cy="7" r="3" />
        <circle cx="17" cy="9" r="3" />
        <path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
        <path d="M17 15c2.2.4 4 2 4 5" />
      </svg>
    ),
    scheduler: (
      <svg viewBox="0 0 24 24" style={s}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
        <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
      </svg>
    ),
    reviews: (
      <svg viewBox="0 0 24 24" style={s}>
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 12h6M9 16h4" />
      </svg>
    ),
    scoring: (
      <svg viewBox="0 0 24 24" style={s}>
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
    eprforms: (
      <svg viewBox="0 0 24 24" style={s}>
        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
    email: (
      <svg viewBox="0 0 24 24" style={s}>
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M2 7l10 7 10-7" />
      </svg>
    ),
    emaillogs: (
      <svg viewBox="0 0 24 24" style={s}>
        <rect x="3" y="4" width="14" height="11" rx="1.5" />
        <path d="M3 7l7 5 7-5" />
        <path d="M15 16l3 3 4-5" />
      </svg>
    ),
    reports: (
      <svg viewBox="0 0 24 24" style={s}>
        <path d="M3 3v18h18" />
        <path d="M7 16l4-4 4 4 4-6" />
      </svg>
    ),
    settings: (
      <svg viewBox="0 0 24 24" style={s}>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
      </svg>
    ),
  };
  return icons[name] || null;
};

const SUB_ADMIN_PAGES = ["employees", "resources", "scheduler", "reports"];

// Sidebar visibility rules:
//   • sub_admin  → only SUB_ADMIN_PAGES
//   • admin      → everything except `superAdminOnly` items (Email Logs)
//   • super_admin→ everything
const isNavVisible = (item, role) => {
  if (role === "sub_admin") return SUB_ADMIN_PAGES.includes(item.id);
  if (item.superAdminOnly) return role === "super_admin";
  return true;
};

const Sidebar = ({ active, setActive, leakageCount, reviewCount, onSignOut, profile, setProfile, isSubAdmin, role }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showChgPwd, setShowChgPwd] = useState(false);
  const [showSignOut, setShowSignOut] = useState(false);
  const menuRef = useRef(null);

  // profileDraft is a local edit buffer — only lives while the modal is open
  const [profileDraft, setProfileDraft] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileErr, setProfileErr] = useState("");

  // Password state
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdErr, setPwdErr] = useState("");
  const [pwdOk, setPwdOk] = useState(false);

  // Close popover on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const openProfile = () => {
    setProfileDraft({ ...profile });
    setProfileErr("");
    setMenuOpen(false);
    setShowProfile(true);
  };

  // Persist profile changes to the backend before updating local state.
  // PUT /api/settings/profile accepts: first_name, last_name, designation, phone, timezone.
  // (Email and role are intentionally NOT editable from this modal.)
  const saveProfile = async () => {
    if (!profileDraft) return;
    if (!profileDraft.firstName || !profileDraft.lastName) {
      setProfileErr("First and last name are required.");
      return;
    }
    setProfileSaving(true);
    setProfileErr("");
    try {
      const res = await apiFetch("/api/settings/profile", {
        method: "PUT",
        body: JSON.stringify({
          first_name: profileDraft.firstName,
          last_name: profileDraft.lastName,
          designation: profileDraft.designation || "",
          phone: profileDraft.phone || "",
          timezone: profileDraft.timezone || "Asia/Kolkata",
        }),
      });
      const d = await res.json();
      if (!d.success) {
        setProfileErr(d.message || "Save failed");
        setProfileSaving(false);
        return;
      }
      // Backend persisted — now sync local state so the sidebar chip + parent
      // profile reflect immediately, no page refresh required.
      const newName = [profileDraft.firstName, profileDraft.lastName].filter(Boolean).join(" ");
      setProfile({
        ...profileDraft,
        name: newName,
        avatar: profileDraft.firstName ? profileDraft.firstName[0].toUpperCase() : (profileDraft.avatar || "?"),
      });
      setProfileSaving(false);
      setShowProfile(false);
    } catch (e) {
      setProfileErr("Network error — profile not saved");
      setProfileSaving(false);
    }
  };

  const submitPwd = () => {
    setPwdErr("");
    if (!pwd.current) { setPwdErr("Current password is required."); return; }
    if (pwd.next.length < 8) { setPwdErr("New password must be at least 8 characters."); return; }
    if (pwd.next !== pwd.confirm) { setPwdErr("New passwords do not match."); return; }
    // In real app: API call here
    setPwdOk(true);
    setTimeout(() => { setShowChgPwd(false); setPwd({ current: "", next: "", confirm: "" }); setPwdOk(false); }, 1600);
  };

  const MENU_ITEMS = [
    { icon: "👤", label: "My Profile", action: openProfile },
    { icon: "🔒", label: "Change Password", action: () => { setMenuOpen(false); setShowChgPwd(true); } },
    ...(!isSubAdmin ? [{ icon: "⚙️", label: "Settings", action: () => { setMenuOpen(false); setActive("settings"); } }] : []),
    { icon: "🚪", label: "Sign Out", action: () => { setMenuOpen(false); setShowSignOut(true); }, danger: true },
  ];

  // Reusable input style
  const inputStyle = {
    width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #E2E8F0",
    fontSize: 13, fontFamily: "'DM Sans',sans-serif", outline: "none", background: "#F8FAFC", color: "#0D1B2A"
  };

  return (
    <>
      {/* ── Sidebar shell ── */}
      <div style={{
        width: 236, minHeight: "100vh", background: "#0D1B2A", display: "flex", flexDirection: "column",
        position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 50, padding: "0 12px",
        boxShadow: "4px 0 24px rgba(0,0,0,.18)"
      }}>
        <div style={{ padding: "24px 8px 20px", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 20, color: "#fff", letterSpacing: -0.5 }}>
            Dolluz Corp<span style={{ color: "#E8520A" }}>.</span>
          </div>
          <div style={{ fontSize: 10.5, color: "#4A6FA5", fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", marginTop: 2 }}>EPR Portal</div>
        </div>
        <nav style={{ flex: 1, padding: "16px 0", display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ fontSize: 10, color: "#4A6FA5", fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", padding: "4px 14px 8px" }}>Main Menu</div>
          {NAV_ITEMS.slice(0, 8).filter(n => isNavVisible(n, role)).map(n => (
            <div key={n.id} className={`sidebar-item${active === n.id ? " active" : ""}`} onClick={() => setActive(n.id)}>
              <NavIcon name={n.iconKey} color={active === n.id ? "#E8520A" : "#4A6FA5"} />
              <span style={{ flex: 1 }}>{n.label}</span>
              {n.id === "reviews" && reviewCount > 0 && <span style={{ background: "#E8520A", color: "#fff", borderRadius: 100, fontSize: 10, fontWeight: 700, padding: "2px 7px" }}>{reviewCount}</span>}
              {n.id === "resources" && leakageCount > 0 && (
                /* #8 — drop the "!" prefix; it visually merges with digits ("!10" → "110").
                   Use a white dot before the count to keep the "alert" feel without ambiguity. */
                <span style={{ background: "#EF4444", color: "#fff", borderRadius: 100, fontSize: 10, fontWeight: 700, padding: "2px 7px", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", display: "inline-block" }} />
                  {leakageCount}
                </span>
              )}
            </div>
          ))}
          {!isSubAdmin && <div style={{ fontSize: 10, color: "#4A6FA5", fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", padding: "12px 14px 8px" }}>Analytics</div>}
          {NAV_ITEMS.slice(8).filter(n => isNavVisible(n, role)).map(n => (
            <div key={n.id} className={`sidebar-item${active === n.id ? " active" : ""}`} onClick={() => setActive(n.id)}>
              <NavIcon name={n.iconKey} color={active === n.id ? "#E8520A" : "#4A6FA5"} />
              <span>{n.label}</span>
            </div>
          ))}
        </nav>

        {/* ── Profile chip ── */}
        <div style={{ padding: "12px 8px 16px", borderTop: "1px solid rgba(255,255,255,.07)", position: "relative" }} ref={menuRef}>

          {/* Popover menu — slides up above the chip */}
          {menuOpen && (
            <div className="pop" style={{
              position: "absolute", bottom: "calc(100% + 6px)", left: 8, right: 8,
              background: "#1A2E45", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,.4)",
              border: "1px solid rgba(255,255,255,.1)", overflow: "hidden", zIndex: 200
            }}>
              <div style={{ padding: "10px 14px 8px", borderBottom: "1px solid rgba(255,255,255,.07)" }}>
                <div style={{ fontSize: 11, color: "#4A6FA5", fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>Account</div>
              </div>
              {MENU_ITEMS.map(item => (
                <div key={item.label} onClick={item.action}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "11px 14px",
                    cursor: "pointer", transition: "background .15s",
                    color: item.danger ? "#FCA5A5" : "#CBD5E1", fontSize: 13, fontWeight: 500
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = item.danger ? "rgba(239,68,68,.15)" : "rgba(255,255,255,.07)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <span style={{ fontSize: 15, width: 20, textAlign: "center" }}>{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          )}

          <div onClick={() => setMenuOpen(o => !o)}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px", borderRadius: 10, cursor: "pointer",
              background: menuOpen ? "rgba(255,255,255,.1)" : "transparent", transition: "background .15s"
            }}
            onMouseEnter={e => { if (!menuOpen) e.currentTarget.style.background = "rgba(255,255,255,.06)"; }}
            onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = "transparent"; }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#E8520A,#FF8C5A)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff", flexShrink: 0
            }}>
              {profile.avatar}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{profile.name}</div>
              <div style={{ fontSize: 11, color: "#4A6FA5", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{profile.designation} · {profile.company}</div>
            </div>
            <div style={{ fontSize: 14, color: menuOpen ? "#E8520A" : "#4A6FA5", transition: "color .15s", flexShrink: 0 }}>&#8942;</div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          MY PROFILE MODAL
      ══════════════════════════════════════════════════════ */}
      {showProfile && profileDraft && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "min(480px, 96vw)", maxHeight: "90vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#0D1B2A,#1A3352)", padding: "24px 28px 20px", borderRadius: "14px 14px 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,#E8520A,#FF8C5A)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "#fff"
                }}>
                  {profileDraft.avatar || (profileDraft.firstName ? profileDraft.firstName[0].toUpperCase() : profileDraft.name ? profileDraft.name[0].toUpperCase() : "S")}
                </div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 17, color: "#fff" }}>My Profile</div>
                  <div style={{ fontSize: 12, color: "#4A6FA5", marginTop: 2 }}>Edit your account information</div>
                </div>
                <button onClick={() => setShowProfile(false)} style={{
                  marginLeft: "auto", background: "rgba(255,255,255,.1)", border: "none",
                  color: "#fff", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center"
                }}>&#215;</button>
              </div>
            </div>
            {/* Body */}
            <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>First Name *</label>
                  <input style={inputStyle} value={profileDraft.firstName || ""}
                    onChange={e => setProfileDraft(p => ({ ...p, firstName: e.target.value, name: [e.target.value, p.lastName].filter(Boolean).join(" "), avatar: e.target.value ? e.target.value[0].toUpperCase() : "S" }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>Last Name *</label>
                  <input style={inputStyle} value={profileDraft.lastName || ""}
                    onChange={e => setProfileDraft(p => ({ ...p, lastName: e.target.value, name: [p.firstName, e.target.value].filter(Boolean).join(" ") }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>Designation</label>
                  <input style={inputStyle} value={profileDraft.designation}
                    onChange={e => setProfileDraft(p => ({ ...p, designation: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>Company</label>
                  <input style={inputStyle} value={profileDraft.company}
                    onChange={e => setProfileDraft(p => ({ ...p, company: e.target.value }))} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>Phone</label>
                  <input style={inputStyle} value={profileDraft.phone} placeholder="+91 98765 43210"
                    onChange={e => setProfileDraft(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>Timezone</label>
                  <select style={{ ...inputStyle, cursor: "pointer" }} value={profileDraft.timezone}
                    onChange={e => setProfileDraft(p => ({ ...p, timezone: e.target.value }))}>
                    {["Asia/Kolkata", "Asia/Dubai", "Europe/London", "America/New_York", "America/Los_Angeles", "UTC"].map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Avatar preview */}
              <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, border: "1.5px solid #E2E8F0" }}>
                <div style={{
                  width: 42, height: 42, borderRadius: "50%", background: "linear-gradient(135deg,#E8520A,#FF8C5A)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff"
                }}>
                  {profileDraft.avatar || "S"}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0D1B2A" }}>{profileDraft.name || "—"}</div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>{profileDraft.designation} · {profileDraft.company}</div>
                </div>
                <div style={{ marginLeft: "auto", fontSize: 10, color: "#94A3B8", fontStyle: "italic" }}>Sidebar preview</div>
              </div>
            </div>
            {profileErr && (
              <div style={{ margin: "0 28px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#B91C1C" }}>
                {profileErr}
              </div>
            )}
            <div style={{ padding: "0 28px 24px", display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn-secondary" disabled={profileSaving} onClick={() => setShowProfile(false)}>Cancel</button>
              <button className="btn-primary" disabled={profileSaving} onClick={saveProfile}>
                {profileSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          CHANGE PASSWORD MODAL
      ══════════════════════════════════════════════════════ */}
      {showChgPwd && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "min(420px, 96vw)" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: "linear-gradient(135deg,#0D1B2A,#1A3352)", padding: "24px 28px 20px", borderRadius: "14px 14px 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 17, color: "#fff" }}>Change Password</div>
                  <div style={{ fontSize: 12, color: "#4A6FA5", marginTop: 2 }}>Keep your account secure</div>
                </div>
                <button onClick={() => { setShowChgPwd(false); setPwd({ current: "", next: "", confirm: "" }); setPwdErr(""); setPwdOk(false); }}
                  style={{ background: "rgba(255,255,255,.1)", border: "none", color: "#fff", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>&#215;</button>
              </div>
            </div>
            <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 14 }}>
              {pwdOk ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#10B981" }}>Password changed successfully!</div>
                </div>
              ) : (
                <>
                  {[["Current Password", "current"], ["New Password", "next"], ["Confirm New Password", "confirm"]].map(([lbl, key]) => (
                    <div key={key}>
                      <label style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: .6 }}>{lbl}</label>
                      <input type="password" style={inputStyle} value={pwd[key]}
                        onChange={e => { setPwdErr(""); setPwd(p => ({ ...p, [key]: e.target.value })); }} />
                    </div>
                  ))}
                  {pwdErr && (
                    <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#B91C1C" }}>
                      {pwdErr}
                    </div>
                  )}
                  <div style={{ background: "#F8FAFC", borderRadius: 8, padding: "10px 14px", fontSize: 11, color: "#64748B", lineHeight: 1.7 }}>
                    Password must be at least 8 characters and should include uppercase, numbers, and special characters.
                  </div>
                </>
              )}
            </div>
            {!pwdOk && (
              <div style={{ padding: "0 28px 24px", display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="btn-secondary" onClick={() => { setShowChgPwd(false); setPwd({ current: "", next: "", confirm: "" }); setPwdErr(""); }}>Cancel</button>
                <button className="btn-primary" onClick={submitPwd}>Update Password</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          SIGN OUT CONFIRM MODAL
      ══════════════════════════════════════════════════════ */}
      {showSignOut && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: "min(400px, 96vw)", textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "36px 28px 24px" }}>
              <div style={{ fontSize: 44, marginBottom: 14 }}>🚪</div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 18, color: "#0D1B2A", marginBottom: 8 }}>Sign out of EPR Portal?</div>
              <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6, marginBottom: 24 }}>
                You'll be returned to the login screen. Any unsaved changes will be lost.
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                <button className="btn-secondary" style={{ minWidth: 100 }} onClick={() => setShowSignOut(false)}>Cancel</button>
                <button className="btn-primary" style={{ background: "#EF4444", minWidth: 120 }}
                  onClick={() => { setShowSignOut(false); if (onSignOut) onSignOut(); }}>
                  Yes, Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;